import { buildAsturHeaders } from '@/endpoints/channel/asturHeaders';
import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import type { CommonalityValueType } from '@/types/commonality';
import { utils } from '@/utils';
import type { WriteRequestOptions } from '../../requestOptions';

export type GlobalCommonalityBulkMapping = Readonly<{
  commonality_key: string;
  default_value_type: CommonalityValueType;
  default_value: string;
  fallback_value_type?: CommonalityValueType | null;
  fallback_value?: string | null;
  grouping_id?: number | null;
  enabled?: boolean;
}>;

export type GlobalCommonalityBulkBody = Readonly<{
  global_set_id: number;
  mappings: GlobalCommonalityBulkMapping[];
}>;

export type GlobalCommonalityBulkResult = Readonly<{
  commonality_key: string;
  id?: number;
  was_created?: boolean;
  error?: string;
}>;

export type GlobalCommonalityBulkData = Readonly<{
  global_set_id: number;
  results: GlobalCommonalityBulkResult[];
  // The per-subscription decorate fan-out now runs asynchronously on the SPC worker pool
  // (a single fan-out SPC is enqueued), so the response reports only that it was queued and
  // how many subscriptions it will affect — not per-sub dispatch/failure results.
  decorate_fanout_enqueued: boolean;
  decorate_affected_subscriptions: number;
}>;

type GlobalCommonalityBulkResponse = Readonly<{
  data: GlobalCommonalityBulkData;
}>;

export async function postGlobalCommonalityBulk(
  scope: RequestScope,
  args: { body: GlobalCommonalityBulkBody },
  options: WriteRequestOptions
): Promise<ChannelApiResponse<GlobalCommonalityBulkData>> {
  try {
    const res = await scopedChannelApi(scope).post<GlobalCommonalityBulkResponse>('atts/commonality/global/bulk', args.body, {
      ...options,
      headers: buildAsturHeaders(),
    });

    if (!res.data?.data) {
      throw new Error('Invalid bulk upsert response');
    }

    return {
      data: res.data.data,
      status: res.status,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}
